'use no memo';

import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid} from 'recharts';
import {useTheme} from 'styled-components';

import {head, last} from 'ramda';

import {PriceChartItem} from './PriceChartItem';
import {PriceChartLabel} from './PriceChartLabel';
import {PriceChartTooltip} from './PriceChartTooltip';

interface PriceChartProps {
  chartData: PriceChartItem[];
  currency: string;
  marginPercentage?: number;
}

export function PriceChart(props: PriceChartProps) {
  const formatNumber = useFormatNumber();
  const formatDate = useDateTimeFormatter();
  const theme = useTheme();

  const marginPercentage = (props.marginPercentage ?? 0) / 100;
  const firstPrice = head(props.chartData)?.price;
  const lastPrice = last(props.chartData)?.price;

  const chartDataWithPrevious = props.chartData.map((item, index) => ({
    ...item,
    previousPrice: props.chartData[index - 1]?.price,
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={chartDataWithPrevious}
        /* CSS is needed to allow label overflow from the chart */
        /* eslint-disable-next-line react/forbid-component-props */
        css={`
          .recharts-surface {
            overflow: visible;
          }
        `}
      >
        <defs>
          <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.colors.general.accent} stopOpacity={0.5} />
            <stop offset="100%" stopColor={theme.colors.general.accent} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={theme.colors.palettes.neutral[30][100]} vertical={false} />
        <Tooltip
          content={(tooltipProps) => (
            <PriceChartTooltip
              active={tooltipProps.active}
              payload={tooltipProps.payload}
              currency={props.currency}
            />
          )}
          cursor={false}
          wrapperStyle={{outline: 'none'}}
          contentStyle={{padding: 0, border: 'none', background: 'transparent'}}
        />
        <XAxis
          dataKey="date"
          type="number"
          domain={['dataMin', 'dataMax']}
          axisLine={false}
          tickLine={false}
          tick={{fontSize: 12, fill: theme.colors.text.secondary}}
          ticks={chartDataWithPrevious.map((item) => item.date)}
          tickFormatter={(value) => formatDate('dateShort', value)}
        />
        <YAxis
          dataKey="price"
          domain={([dataMin, dataMax]: [number, number]) => {
            const padding = Math.abs(dataMax - dataMin) * marginPercentage;
            return [dataMin - padding, dataMax + padding];
          }}
          axisLine={false}
          tickLine={false}
          tick={{fontSize: 12, fill: theme.colors.text.secondary}}
          tickFormatter={(value) => formatNumber(value, 0) ?? ''}
          width={72}
        />
        <Area
          type="stepAfter"
          dataKey="price"
          stroke={theme.colors.general.accent}
          fill="url(#priceGradient)"
          strokeWidth={1}
          isAnimationActive={false}
          label={(labelProps: {viewBox: {x: number; y: number}; index: number}) => (
            <PriceChartLabel
              viewBox={labelProps.viewBox}
              index={labelProps.index}
              totalPoints={chartDataWithPrevious.length}
              firstPrice={firstPrice}
              lastPrice={lastPrice}
              currency={props.currency}
            />
          )}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
